import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { COUNTRY_NAMES, LEAD_TYPES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  type: "distributor",
  company: "",
  name: "",
  email: "",
  phone: "",
  country: "",
  brands: "",
  installations: "",
  volume: "",
  message: "",
  consent: false,
};

export default function LeadForm({
  defaultType = "distributor",
  context = "",
  showTypeSelect = false,
  onSuccess,
}) {
  const [form, setForm] = useState({ ...EMPTY, type: defaultType });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the privacy consent to continue.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, context });
      toast.success("Request received. Our team will reply within 48 hours.");
      setForm({ ...EMPTY, type: defaultType });
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldCls =
    "h-11 rounded-lg border-slate-200 bg-white text-sm focus-visible:ring-signal";

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-4">
      {showTypeSelect && (
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">
            Request type
          </label>
          <Select value={form.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger data-testid="lead-type-select" className={fieldCls}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LEAD_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Company *</label>
          <Input required data-testid="lead-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} className={fieldCls} placeholder="Your company" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Contact name *</label>
          <Input required data-testid="lead-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={fieldCls} placeholder="Full name" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Email *</label>
          <Input required type="email" data-testid="lead-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} placeholder="name@company.com" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Phone</label>
          <Input data-testid="lead-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={fieldCls} placeholder="+39 ..." />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Country *</label>
          <Select value={form.country} onValueChange={(v) => set("country", v)} required>
            <SelectTrigger data-testid="lead-country-select" className={fieldCls}>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_NAMES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Brands currently sold</label>
          <Input data-testid="lead-brands-input" value={form.brands} onChange={(e) => set("brands", e.target.value)} className={fieldCls} placeholder="e.g. Stannah, Acorn" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Installations / year</label>
          <Input data-testid="lead-installations-input" value={form.installations} onChange={(e) => set("installations", e.target.value)} className={fieldCls} placeholder="e.g. 150" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Est. purchase volume</label>
          <Input data-testid="lead-volume-input" value={form.volume} onChange={(e) => set("volume", e.target.value)} className={fieldCls} placeholder="e.g. €500k / year" />
        </div>
      </div>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Message</label>
        <Textarea data-testid="lead-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} className="rounded-lg border-slate-200 text-sm focus-visible:ring-signal" placeholder="Tell us about your business and objectives" />
      </div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="lead-consent-wrapper">
        <Checkbox
          data-testid="lead-consent-checkbox"
          checked={form.consent}
          onCheckedChange={(v) => set("consent", v === true)}
          className="mt-0.5"
        />
        <span className="text-xs text-slate-500 leading-relaxed">
          I consent to ACTIVE Stairlifts processing my data to respond to this enquiry, in accordance with GDPR. *
        </span>
      </label>
      <button
        type="submit"
        disabled={loading}
        data-testid="lead-submit-button"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-obsidian text-white text-sm font-medium px-6 py-3.5 hover:bg-signal transition-colors duration-300 disabled:opacity-50"
      >
        {loading ? "Sending..." : "Submit Request"}
        {!loading && <ArrowRight size={15} />}
      </button>
    </form>
  );
}

export const LeadDialog = ({ trigger, defaultType = "distributor", title, context = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{title}</DialogTitle>
        </DialogHeader>
        <LeadForm defaultType={defaultType} context={context} onSuccess={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};
